import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import get from 'lodash.get';

/* Helpers */
import { getProductTileAdMap } from '../../utils/adHelpers';
import { locations } from '../../utils/tracking/shared/favoritesTracking';

/* Components */
import { TradeRewardsMessagingWrapper } from '../../components/global/TradeRewardsMessagingWrapper';
import { ProductTileAd } from '../../components/global/Ads/ProductTileAd';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { SbSharedItemTrackingProvider } from '../SbSharedItemTrackingProvider/SbSharedItemTrackingProvider';
import { SbSharedMeasurementUnitProvider } from '../SbSharedMeasurementUnitProvider/SbSharedMeasurementUnitProvider';
import { SbRespSearchResultGrid, PROMINENT_TILE } from './SbRespSearchResultGrid';
import { SbRespProminentTileDescription } from '../SbRespProminentTileDescription/SbRespProminentTileDescription';

import styles from './sbRespSearchResultContainer.scss';

import { SbRespVisuallySimilarLink } from '../SbRespVisuallySimilarLink/SbRespVisuallySimilarLink';
import { SbRespNegotiable } from '../SbRespNegotiable/SbRespNegotiable';

/* Constants */
import { VERTICAL_OFFSET_SB_RESP_IMG } from '../../constants/imageConstants';

const ITEMS_PER_ROW = 3;
const LAZY_LOAD_ITEM_TILE_INDEX = ITEMS_PER_ROW * 4;

const SbRespSearchResultContainerComponent = props => {
    const {
        currency,
        showMeasurements,
        showInternalSellerName,
        showSellerName,
        showCreatorName,
        searchBrowse,
        viewer,
        relay,
        isClient,
        user,
        renderVariables,
        pageSize,
        gridType,
    } = props;

    const { edges: items = [], displayUriRef } = searchBrowse || {};
    const {
        isTileAdEnabled,
        isFavoritesEnabled,
        isSeoQuicklookEnabled,
        isVisuallySimilarEnabled,
    } = viewer;

    const productTileAdMap = getProductTileAdMap({
        uriRef: displayUriRef,
        pageSize,
    });

    const userId = user ? user.serviceId : null;

    /* eslint-disable react/prop-types, react/display-name */
    return (
        <div className={styles.container}>
            <FavoritesProvider
                disable={!isClient}
                environment={relay.environment}
                itemIds={items.map(({ node }) => node.serviceId)}
                location={locations.SEARCH_BROWSE}
                userId={userId}
                renderVariables={renderVariables}
            >
                {({ props: favorites }) => (
                    <QuickViewProvider>
                        {({ quickView }) => (
                            <SbSharedItemTrackingProvider searchBrowse={searchBrowse}>
                                {({ fireItemImpressionTracking, fireItemClickTracking }) => (
                                    <SbRespSearchResultGrid items={items} type={gridType}>
                                        {({ item, index, customTileWidthClass, tileType }) => {
                                            const ad = productTileAdMap[index + 1]; // 1-indexed
                                            const quickViewProps = isSeoQuicklookEnabled
                                                ? quickView
                                                : null;
                                            let adEl;

                                            if (ad) {
                                                const { adId, elementId } = ad;
                                                if (get(user, 'tradeFirm.serviceId')) {
                                                    adEl = (
                                                        <TradeRewardsMessagingWrapper
                                                            environment={relay.environment}
                                                            user={user}
                                                        />
                                                    );
                                                } else {
                                                    adEl = (
                                                        <ProductTileAd
                                                            adId={adId}
                                                            elementId={elementId}
                                                        />
                                                    );
                                                }
                                            }

                                            const itemId = get(item, 'serviceId') || '';
                                            const isProminentTile = tileType === PROMINENT_TILE;

                                            const children = ({ tileHoveredClass }) => (
                                                <Fragment>
                                                    <SbRespNegotiable item={item} />

                                                    {isProminentTile && (
                                                        <SbRespProminentTileDescription
                                                            item={item}
                                                        />
                                                    )}

                                                    {isVisuallySimilarEnabled && (
                                                        <div
                                                            className={`${
                                                                styles.visuallySimilarWrapper
                                                            } ${tileHoveredClass}`}
                                                        >
                                                            <SbRespVisuallySimilarLink
                                                                itemId={itemId}
                                                                isClient={!!isClient}
                                                            />
                                                        </div>
                                                    )}
                                                </Fragment>
                                            );
                                            const tileImageSize = isProminentTile
                                                ? 'large'
                                                : undefined;

                                            return (
                                                <Fragment>
                                                    {isTileAdEnabled && adEl}
                                                    <SbSharedMeasurementUnitProvider
                                                        user={user}
                                                        searchBrowse={searchBrowse}
                                                    >
                                                        {({ measurementUnit }) => (
                                                            <SharedItemTile
                                                                item={item}
                                                                favorites={
                                                                    isFavoritesEnabled && favorites
                                                                }
                                                                index={index}
                                                                currency={currency}
                                                                isMobile={false}
                                                                showInternalSellerName={
                                                                    showInternalSellerName
                                                                }
                                                                showSellerName={showSellerName}
                                                                showCreatorName={showCreatorName}
                                                                showMeasurements={showMeasurements}
                                                                quickViewProps={quickViewProps}
                                                                children={children}
                                                                lazyLoadStartIndex={
                                                                    LAZY_LOAD_ITEM_TILE_INDEX
                                                                }
                                                                imageLoadVerticalOffset={
                                                                    VERTICAL_OFFSET_SB_RESP_IMG
                                                                }
                                                                onContentVisible={() =>
                                                                    fireItemImpressionTracking(
                                                                        index
                                                                    )
                                                                }
                                                                onClick={() =>
                                                                    fireItemClickTracking(index)
                                                                }
                                                                measurementUnit={measurementUnit}
                                                                customTileWidthClass={
                                                                    customTileWidthClass
                                                                }
                                                                imageSize={tileImageSize}
                                                            />
                                                        )}
                                                    </SbSharedMeasurementUnitProvider>
                                                </Fragment>
                                            );
                                        }}
                                    </SbRespSearchResultGrid>
                                )}
                            </SbSharedItemTrackingProvider>
                        )}
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
        </div>
    );
    /* eslint-enable react/prop-types, react/display-name */
};

SbRespSearchResultContainerComponent.propTypes = {
    currency: PropTypes.string.isRequired,
    showMeasurements: PropTypes.bool.isRequired,
    showInternalSellerName: PropTypes.bool,
    showSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    searchBrowse: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    isClient: PropTypes.bool,
    user: PropTypes.object,
    pageSize: PropTypes.number.isRequired,
    renderVariables: PropTypes.object.isRequired,
    gridType: PropTypes.string,
};

function mapStateToProps(state) {
    return {
        renderVariables: get(state, 'relayVariables.variables'),
        pageSize: get(state, 'relayVariables.variables.first'),
    };
}

export const SbRespSearchResultContainer = createFragmentContainer(
    connect(mapStateToProps)(SbRespSearchResultContainerComponent),
    {
        viewer: graphql`
            fragment SbRespSearchResultContainer_viewer on Viewer {
                isSeoQuicklookEnabled: isEnabledByLocale(featureName: "SEO_QUICKLOOK")
                isTileAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isFavoritesEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isVisuallySimilarEnabled: isEnabledByLocale(featureName: "VISUALLY_SIMILAR")
            }
        `,
        user: graphql`
            fragment SbRespSearchResultContainer_user on User {
                serviceId
                tradeFirm @include(if: $isTrade) {
                    serviceId
                }
                ...SbSharedMeasurementUnitProvider_user
                ...TradeRewardsMessagingWrapper_user
            }
        `,
        searchBrowse: graphql`
            fragment SbRespSearchResultContainer_searchBrowse on SearchBrowseQueryConnection {
                ...SbSharedMeasurementUnitProvider_searchBrowse
                ...SbSharedItemTrackingProvider_searchBrowse
                displayUriRef
                edges {
                    node {
                        serviceId
                        ...SbRespProminentTileDescription_item
                        ...SharedItemTile_item
                        ...SbRespNegotiable_item
                    }
                }
            }
        `,
    }
);
